import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";

const LABEL_IMG =
  "https://images.pexels.com/photos/12349173/pexels-photo-12349173.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const pillars = [
  {
    title: "Full Ownership",
    description:
      "Every artist under 3rd Degree retains 100% ownership of their masters and creative direction.",
  },
  {
    title: "Independent Power",
    description:
      "No major label strings attached. We move on our own terms, at our own pace, with our own vision.",
  },
  {
    title: "Built Through Fire",
    description:
      "3rd Degree was born from adversity. The name represents the highest level of transformation — third degree heat.",
  },
];

export default function Label() {
  return (
    <div className="pt-16">
      {/* Header */}
      <div className="relative py-24 sm:py-32 px-6 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-8"
          style={{ backgroundImage: `url(${LABEL_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B0000]/15 to-transparent" />

        <div className="max-w-6xl mx-auto relative z-10">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
            The Label
          </p>
          <h1
            data-testid="label-page-title"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl mb-4"
          >
            3RD DEGREE ENT.
          </h1>
          <div className="w-24 h-1 bg-[#8B0000] mb-8" />
          <p className="text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed">
            3rd Degree Entertainment is more than a label — it's a declaration
            of independence. Founded by Luxifer 6, the label exists to prove
            that artists can own their work, control their narrative, and build
            empires from the ground up.
          </p>
        </div>
      </div>

      {/* Pillars */}
      <div className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection testId="label-pillars" className="mb-16">
            <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
              Core Pillars
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl mb-12">
              WHAT WE STAND FOR
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <AnimatedSection
                key={pillar.title}
                testId={`pillar-${index + 1}`}
                delay={index * 150}
              >
                <div className="bg-[#111111] border border-white/5 p-8 h-full hover:border-[#8B0000]/30 transition-colors duration-500">
                  <div className="w-10 h-10 border border-[#8B0000] flex items-center justify-center font-heading text-xl text-[#D4AF37] mb-6">
                    {index + 1}
                  </div>
                  <h3 className="font-heading text-2xl mb-3">{pillar.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>

      {/* Mission statement */}
      <AnimatedSection
        testId="label-mission"
        className="py-24 px-6 bg-[#0A0A0A] relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B0000]/5 to-transparent" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="border-l-4 border-[#D4AF37] pl-8 sm:pl-12">
            <p className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8">
              "WE DON'T JUST MAKE MUSIC.
              <span className="text-[#D4AF37]"> WE BUILD LEGACIES.</span>"
            </p>
            <p className="text-white/50 text-sm">
              — Luxifer 6, Founder of 3rd Degree Ent.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection testId="label-cta" className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl mb-6">
            WANT TO WORK WITH US?
          </h2>
          <p className="text-white/50 mb-8">
            If you share the vision of ownership, independence, and
            transformation, reach out.
          </p>
          <Button
            data-testid="label-contact-btn"
            asChild
            className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-sm h-auto"
          >
            <Link to="/contact">
              Get In Touch <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </AnimatedSection>
    </div>
  );
}
